import { Text, TextProps, dateInputDateFormat } from '@bitrise/bitkit';
import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon';
import cronstrue from 'cronstrue';
import { format } from 'date-fns';
import { getWeekOfMonthStr } from '../../../components/BuildConfiguration/BasicBuildSchedule';
import { getDayDifference, moveDay } from './utils/buildScheduleUtils';

interface BuildScheduleDateTimeProps extends TextProps {
  nextRun: Date;
  setupLocation?: string;
  weekdayIndexes?: number[];
  repetitionEnabled?: boolean;
  repeatEvery?: number;
  repeatInterval?: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
  startDate?: string;
  monthlyRepetition?: 'day' | 'weekday';
  yearlyRepetition?: 'day' | 'weekday';
  cronExpression?: string;
}

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const getBuildScheduleDate = ({
  nextRun,
  repetitionEnabled,
  repeatEvery,
  repeatInterval,
  startDate,
  setupLocation,
  monthlyRepetition,
  yearlyRepetition,
  weekdayIndexes,
}: Omit<BuildScheduleDateTimeProps, 'cronExpression'>) => {
  const date = startDate && DateTime.fromFormat(startDate, dateInputDateFormat);
  const suffix = repeatEvery && repeatEvery > 1 ? 's' : '';

  let timeDescription;

  if (!repetitionEnabled && date) {
    timeDescription = `On ${date.monthShort} ${date.day}, ${date.year}`;
  } else if (weekdayIndexes) {
    const dayDiff = getDayDifference(nextRun, Intl.DateTimeFormat().resolvedOptions().timeZone, setupLocation);
    const adjustedWeekDayIndexes = weekdayIndexes.map((day) => moveDay(day, (-1 * Math.sign(dayDiff)) as 0 | 1 | -1));
    const sortedWeekdayIndexes = [...adjustedWeekDayIndexes].sort();
    let day;
    if (isEqual(sortedWeekdayIndexes, [0, 1, 2, 3, 4])) {
      day = 'weekday';
    } else if (isEqual(sortedWeekdayIndexes, [0, 1, 2, 3, 4, 5, 6])) {
      day = 'every day';
    } else {
      const weekdayNames = sortedWeekdayIndexes.map((aWeekdayIndex) => weekdays[aWeekdayIndex].substring(0, 3));
      day = weekdayNames.join(', ');
    }
    if (repeatEvery && repeatEvery > 1) {
      timeDescription = `Every ${repeatEvery} week${suffix} on ${day}`;
    } else {
      timeDescription = `Every ${day.replace('every ', '')}`;
    }
  } else if (repeatInterval === 'month' && date) {
    if (monthlyRepetition === 'day') {
      timeDescription = `Every ${repeatEvery} month${suffix} on day ${date.day}`;
    } else if (monthlyRepetition === 'weekday') {
      timeDescription = `Every ${repeatEvery} month${suffix} on the ${getWeekOfMonthStr(date)} ${date.weekdayLong}`;
    }
  } else if (repeatInterval === 'year' && date) {
    if (yearlyRepetition === 'day') {
      timeDescription = `Every ${repeatEvery} year${suffix} on ${date.monthShort} ${date.day}`;
    } else if (yearlyRepetition === 'weekday') {
      timeDescription = `Every ${repeatEvery} year${suffix} on the ${getWeekOfMonthStr(date)} ${date.weekdayLong} of ${date.monthLong}`;
    }
  }

  return timeDescription;
};

const BuildScheduleDateTime = ({
  nextRun,
  repetitionEnabled,
  repeatEvery,
  repeatInterval,
  startDate,
  monthlyRepetition,
  yearlyRepetition,
  setupLocation,
  weekdayIndexes,
  cronExpression,
  ...rest
}: BuildScheduleDateTimeProps) => {
  let timeDescription;

  const suffix = repeatEvery && repeatEvery > 1 ? 's' : '';

  if (cronExpression) {
    timeDescription = cronstrue.toString(cronExpression, {
      throwExceptionOnParseError: false,
    });
  } else if (repeatInterval === 'minute' && repeatEvery) {
    timeDescription = `Every ${repeatEvery} minute${suffix}`;
  } else if (repeatInterval === 'hour' && repeatEvery) {
    timeDescription = `Every ${repeatEvery} hour${suffix}`;
  } else if (repeatInterval === 'day' && repeatEvery) {
    timeDescription = `Every ${repeatEvery} day${suffix}`;
  } else {
    timeDescription = `${getBuildScheduleDate({
      nextRun,
      repetitionEnabled,
      repeatEvery,
      repeatInterval,
      startDate,
      setupLocation,
      monthlyRepetition,
      yearlyRepetition,
      weekdayIndexes,
    })} at ${format(nextRun, 'hh:mmaaa')}`;
  }

  return (
    <Text as="time" data-testid="buildScheduleDate" hasEllipsis maxWidth="26rem" title={timeDescription} {...rest}>
      {timeDescription.replace(' AM', 'am').replace(' PM', 'pm')}
    </Text>
  );
};

export default BuildScheduleDateTime;
